import PortalSidebar from "../PortalSidebar/PortalSidebar";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";

/* ───────────────── Pagination helper ────────────────────────── */
const Pagination = ({ page, perPage, total, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {first}-{last} of {total}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

/* ─────────────────────────── Component ─────────────────────── */
export default function LevyAccountViewPage() {
  const { accountId, buildingId } = useParams();
  const navigate = useNavigate();
  const buildingname = localStorage.getItem("buildingName");
  /* ---------- state ---------- */
  const [accountInfo, setAccountInfo] = useState(null);
  const [legals, setLegals] = useState([]);
  const [aods, setAods] = useState([]);
  const [disconnections, setDisconnections] = useState([]);
  const [agreements, setAgreements] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("General");

  /* table-paging */
  const perPage = 10;
  const [aodPage, setAodPage] = useState(1);
  const [legPage, setLegPage] = useState(1);
  const [disPage, setDisPage] = useState(1);
  const [agrPage, setAgrPage] = useState(1);

  /* ---------- helpers ---------- */
  const fmtDate = (d) => {
    try {
      return format(new Date(d), "dd MMM yyyy");
    } catch {
      return d || "—";
    }
  };

  const fmtAmt = (v) =>
    isNaN(parseFloat(v))
      ? v ?? "—"
      : "R " +
        parseFloat(v)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  /* ---------- data fetch ---------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");

    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [accRes, legRes, aodRes, discRes] = await Promise.all([
          fetch(`https://portal.gmacc.co.za/api/levyaccount/${accountId}/`, {
            headers,
          }),
          fetch(
            `https://portal.gmacc.co.za/api/levyaccount/${accountId}/legals/`,
            { headers }
          ),
          fetch(
            `https://portal.gmacc.co.za/api/levyaccount/${accountId}/aods/`,
            { headers }
          ),
          fetch(
            `https://portal.gmacc.co.za/api/levyaccount/${accountId}/disconnections/`,
            { headers }
          ),
        ]);

        if (![accRes, legRes, aodRes, discRes].every((r) => r.ok))
          throw new Error("Failed to load levy-account data");

        const acc = await accRes.json();
        const leg = await legRes.json();
        const aod = await aodRes.json();
        const disc = await discRes.json();

        document.title = `${acc.cj_sageaccountnumber} • Levy Account Detail`;

        setAccountInfo(acc);
        setLegals(Array.isArray(leg) ? leg : leg.value || []);
        setAods(Array.isArray(aod) ? aod : aod.value || []);
        setDisconnections(Array.isArray(disc) ? disc : disc.value || []);

        /* 30-day agreements use the UNIT guid */
        if (acc._cj_unitname_value) {
          const agrRes = await fetch(
            `https://portal.gmacc.co.za/api/unit/${acc.cj_gmaaccountnumberid}/30-day-agreements/`,
            { headers }
          );
          if (agrRes.ok) {
            const agr = await agrRes.json();
            setAgreements(Array.isArray(agr) ? agr : agr.value || []);
          }
        }
      } catch (e) {
        console.error(e);
        setError("Could not load levy account details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [accountId, navigate]);

  /* ---------- load / error ---------- */
  if (loading)
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  if (error) return <p className={styles.errorText}>{error}</p>;
  if (!accountInfo) return null;

  /* ---------- derived ---------- */
  const creditControlOn =
    accountInfo[
      "cj_applycreditcontrol@OData.Community.Display.V1.FormattedValue"
    ] === "Yes";

  const tabs = creditControlOn
    ? ["General", "AODs", "Legals", "Disconnections", "30-Day Agreements"]
    : ["General"];

  // Only red if "Is At Legal" === "Yes"
  const atLegal =
    accountInfo["cj_legal@OData.Community.Display.V1.FormattedValue"] === "Yes";

  /* ---------- field lists ---------- */
  const summaryFields = [
    ["Sage Account Number", "cj_sageaccountnumber"],
    ["Unit", "_cj_unitname_value@OData.Community.Display.V1.FormattedValue"],
    [
      "Building",
      "_cj_linkedbuilding_value@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Account Type",
      "ab_accounttype@OData.Community.Display.V1.FormattedValue",
    ],
    ["Responsible", "cj_responsablepartyfullname"],
    ["Mobile", "cj_responsiblepartymobile"],
    ["Email", "cj_statementemail1"],
    ["Total Outstanding", "cj_agetotal"],
  ];

  const ageFields = [
    ["Total", "cj_agetotal"],
    ["Current", "cj_agecurrent"],
    ["30 Days", "cj_age30days"],
    ["60 Days", "cj_age60days"],
    ["90 Days", "cj_age90days"],
    ["120 Days", "cj_age120days"],
    ["150 Days", "cj_age150days"],
    ["180 Days", "cj_age180days"],
  ];

  const flagFields = [
    ["Is At Legal", "cj_legal@OData.Community.Display.V1.FormattedValue"],
    [
      "Active Arrangement(AOD)",
      "cj_activeaod@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Charge Interest",
      "cj_chargeinteresttoacc@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Charge Interest Reason",
      "cj_reasonnointerest@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Disconnect Override",
      "cj_applydisconnectoverride@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Disconnect Override Reason",
      "cj_reasonfornodisconnect@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Credit Control",
      "cj_applycreditcontrol@OData.Community.Display.V1.FormattedValue",
    ],
    [
      "Credit Control Reason",
      "cj_reasonfornocreditcontrol@OData.Community.Display.V1.FormattedValue",
    ],
  ];

  /* ---------- table helpers ---------- */
  const headCells = (labels) => (
    <thead>
      <tr>
        {labels.map((l) => (
          <th key={l} className={styles.topTableHeader}>
            {l}
          </th>
        ))}
      </tr>
    </thead>
  );

  const td = (val, i, extra = {}) => (
    <td key={i} className={styles.topTableCell} {...extra}>
      {val}
    </td>
  );
  /* ─────────────────────────── render ───────────────────────── */
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {buildingname} - {accountInfo.cj_sageaccountnumber} | Levy Account
            Detail
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>
      <div className={styles.shell}>
        <PortalSidebar
  activePortal="trustee"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        {/* ACCOUNT SUMMARY ------------------------------------- */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            <div className={styles.paymentHeaderCard}>
              <div
                className={`${styles.card} ${
                  atLegal ? styles.negativeCard : ""
                }`}
              >
                {/* BACK BUTTON */}
                <button
                  className={styles.paymentBackBtn}
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                {/* TITLE */}
                <h1 className={styles.paymentHeaderTitle}>ACCOUNT SUMMARY</h1>

                {/* DIVIDER */}
                <div className={styles.paymentHeaderDivider} />

                {/* LABEL ROW — AUTO GRID */}
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": summaryFields.length }}
                >
                  {summaryFields.map(([label]) => (
                    <div key={label} className={styles.paymentHeaderLabelItem}>
                      {label}:
                    </div>
                  ))}
                </div>

                {/* VALUE PILL — AUTO GRID */}
                <div className={styles.paymentHeaderValuePill}>
                  <div
                    className={styles.paymentHeaderGridAuto}
                    style={{ "--col-count": summaryFields.length }}
                  >
                    {summaryFields.map(([label, key]) => {
                      let val =
                        key === "cj_agetotal"
                          ? fmtAmt(accountInfo[key])
                          : accountInfo[key] ?? "—";

                      const isBuilding =
                        label === "Building" &&
                        accountInfo._cj_linkedbuilding_value;

                      return (
                        <div
                          key={key}
                          className={styles.paymentHeaderValueItem}
                        >
                          {isBuilding ? (
                            <span
                              className={styles.relatedLinkDark}
                              onClick={() =>
                                navigate(
                                  `/building-info/${accountInfo._cj_linkedbuilding_value}`
                                )
                              }
                            >
                              {val}
                            </span>
                          ) : (
                            val
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* TABS ------------------------------------------------- */}
            <div className={styles.subTabs}>
              {tabs.map((t) => (
                <div
                  key={t}
                  className={
                    activeTab === t ? styles.subTabActive : styles.subTab
                  }
                  onClick={() => {
                    setActiveTab(t);
                    setAodPage(1);
                    setLegPage(1);
                    setDisPage(1);
                    setAgrPage(1);
                  }}
                >
                  {t}
                </div>
              ))}
            </div>

            {/* GENERAL --------------------------------------------- */}
            {activeTab === "General" && (
              <div className={styles.paymentCardGrid}>
                {/* Age Analysis */}
                <div className={styles.paymentCardDark}>
                  <h2>Age Analysis</h2>
                  {ageFields.map(([lbl, key]) => (
                    <div key={key} className={styles["input-group"]}>
                      <h3>{lbl}:</h3>
                      <input
                        readOnly
                        value={fmtAmt(accountInfo[key])}
                        className={styles.ageAmountInput}
                      />
                    </div>
                  ))}
                </div>

                {/* Flags */}
                {creditControlOn && (
                  <div className={styles.paymentCard}>
                    <h2>Flags</h2>
                    {flagFields.map(([lbl, key]) => {
                      const chargeInterest =
                        accountInfo[
                          "cj_chargeinteresttoacc@OData.Community.Display.V1.FormattedValue"
                        ];
                      const disOverride =
                        accountInfo[
                          "cj_applydisconnectoverride@OData.Community.Display.V1.FormattedValue"
                        ];

                      if (
                        (lbl.startsWith("Charge Interest") &&
                          chargeInterest !== "No") ||
                        (lbl.startsWith("Disconnect Override") &&
                          disOverride !== "No")
                      )
                        return null;

                      return (
                        <div key={key} className={styles["input-group"]}>
                          <h3>{lbl}:</h3>
                          <input readOnly value={accountInfo[key] ?? "—"} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* AODs ------------------------------------------------- */}
            {activeTab === "AODs" && creditControlOn && (
              <div className={styles.paymentCard}>
                <h2>Active AODs</h2>
                {aods.length === 0 ? (
                  <p>No records.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={`${styles.dataTable}`}>
                        {headCells([
                          "AOD Number",
                          "Balance",
                          "Term (m)",
                          "Start",
                          "End",
                        ])}
                        <tbody>
                          {aods
                            .slice((aodPage - 1) * perPage, aodPage * perPage)
                            .map((a) => (
                              <tr key={a.cj_aodid}>
                                {td(
                                  <span
                                    className={styles.linkLike}
                                    onClick={() =>
                                      navigate(
                                        `/aod/${a.cj_aodid}/${buildingId}`
                                      )
                                    }
                                  >
                                    {a.cj_aodnumber}
                                  </span>,
                                  0
                                )}
                                {td(fmtAmt(a.cj_balanceondateofapplication), 1)}
                                {td(a.cj_paymentterminmonths, 2)}
                                {td(fmtDate(a.cj_aodstartdate), 3)}
                                {td(fmtDate(a.cj_aodenddate), 4)}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={aodPage}
                      perPage={perPage}
                      total={aods.length}
                      onChange={setAodPage}
                    />
                  </>
                )}
              </div>
            )}

            {/* LEGALS ---------------------------------------------- */}
            {activeTab === "Legals" && creditControlOn && (
              <div className={styles.paymentCard}>
                <h2>Active Legal Files</h2>
                {legals.length === 0 ? (
                  <p>No records.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={`${styles.dataTable}`}>
                        {headCells([
                          "Name",
                          "Attorney",
                          "Progress",
                          "Debt on Handover",
                          "Next Follow-Up",
                        ])}
                        <tbody>
                          {legals
                            .slice((legPage - 1) * perPage, legPage * perPage)
                            .map((l) => (
                              <tr key={l.cj_legalid}>
                                {td(
                                  <span
                                    className={styles.linkLike}
                                    onClick={() =>
                                      navigate(
                                        `/legal/${l.cj_legalid}/${buildingId}`
                                      )
                                    }
                                  >
                                    {l.cj_name}
                                  </span>,
                                  0
                                )}
                                {td(
                                  l[
                                    "_cj_attorney_value@OData.Community.Display.V1.FormattedValue"
                                  ],
                                  1
                                )}
                                {td(
                                  l[
                                    "cj_progress@OData.Community.Display.V1.FormattedValue"
                                  ],
                                  2
                                )}
                                {td(fmtAmt(l.cj_debtonhandover), 3)}
                                {td(fmtDate(l.cj_nextfollowupdate), 4)}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={legPage}
                      perPage={perPage}
                      total={legals.length}
                      onChange={setLegPage}
                    />
                  </>
                )}
              </div>
            )}

            {/* DISCONNECTIONS -------------------------------------- */}
            {activeTab === "Disconnections" && creditControlOn && (
              <div className={styles.paymentCard}>
                <h2>Active Disconnections</h2>
                {disconnections.length === 0 ? (
                  <p>No records.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={`${styles.dataTable}`}>
                        {headCells([
                          "Debtor",
                          "Status",
                          "Meter",
                          "Instruction",
                        ])}
                        <tbody>
                          {disconnections
                            .slice((disPage - 1) * perPage, disPage * perPage)
                            .map((d) => (
                              <tr key={d.cj_disconnectionid}>
                                {td(
                                  d[
                                    "_cj_debtor_value@OData.Community.Display.V1.FormattedValue"
                                  ],
                                  0
                                )}
                                {td(
                                  d[
                                    "cj_status@OData.Community.Display.V1.FormattedValue"
                                  ],
                                  1
                                )}
                                {td(d.cj_meterreadingondisconnection, 2)}
                                {td(fmtDate(d.cj_dateofinstruction), 3)}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={disPage}
                      perPage={perPage}
                      total={disconnections.length}
                      onChange={setDisPage}
                    />
                  </>
                )}
              </div>
            )}

            {/* 30-DAY AGREEMENTS ----------------------------------- */}
            {activeTab === "30-Day Agreements" && creditControlOn && (
              <div className={styles.paymentCard}>
                <h2>30-Day Agreements</h2>
                {agreements.length === 0 ? (
                  <p>No records.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={`${styles.dataTable}`}>
                        {headCells([
                          "Applicant",
                          "Debt on App.",
                          "Approved On",
                          "Ends On",
                        ])}
                        <tbody>
                          {agreements
                            .slice((agrPage - 1) * perPage, agrPage * perPage)
                            .map((a) => (
                              <tr key={a.cj_dayagreementid}>
                                {td(a.cj_applicantfullname, 0)}
                                {td(
                                  fmtAmt(a.cj_outstandingdebtonapplicationdate),
                                  1
                                )}
                                {td(fmtDate(a.cj_applicationapprovaldate), 2)}
                                {td(fmtDate(a.cj_agreementenddate), 3)}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={agrPage}
                      perPage={perPage}
                      total={agreements.length}
                      onChange={setAgrPage}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
